// Компонент создания фильтра по датам «с — по» для выбора диапазона дат.
import { useEffect, useState } from 'react';
import { DatePickerInput } from '@mantine/dates';
import type { DatesRangeValue, DateStringValue } from '@mantine/dates';
import { useRepositoryFilter } from './useRepositoryFilter';

type RangeValue = DatesRangeValue<DateStringValue>;

type DateRangeFilterOptions = {
  separator?: string;
  serializeDate?: (value: string) => string;
};

// Accept both calendar dates and ISO timestamps from existing saved URLs.
function parseDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

/** Shared period picker, matching the CRM leads filter, with optional boundaries. */
export function createDateRangeFilter<TRepo>(
  key: string,
  placeholder: string,
  applyRange: (repo: TRepo, from: string | null, to: string | null) => TRepo,
  { separator = '|', serializeDate = value => value }: DateRangeFilterOptions = {}
) {
  return useRepositoryFilter<TRepo>(key, ({ filter, disabled, fromUrl, toUrl }) => {
    const [range, setRange] = useState<RangeValue>([null, null]);
    const serialize = (value: string | null) => (value ? serializeDate(value) : null);

    useEffect(() => {
      const raw = fromUrl();
      if (!raw) return;
      const [rawFrom, rawTo] = raw.split(separator);
      const from = parseDate(rawFrom);
      const to = parseDate(rawTo);
      setRange([from, to]);
      if (from || to) {
        filter(repo => applyRange(repo, serialize(from), serialize(to)));
      }
      // Restore once on mount; subsequent changes come from the picker.
    }, []);

    const handleChange = (value: RangeValue) => {
      setRange(value);
      const [from, to] = value.map(serialize);
      if (from || to) {
        filter(repo => applyRange(repo, from, to));
        toUrl(`${from ?? ''}${separator}${to ?? ''}`);
      } else {
        filter(null);
        toUrl(null);
      }
    };

    return (
      <DatePickerInput
        type='range'
        allowSingleDateInRange
        placeholder={placeholder}
        aria-label={placeholder}
        value={range}
        onChange={handleChange}
        valueFormat='DD.MM.YYYY'
        size='xs'
        clearable
        clearButtonProps={{ 'aria-label': `Очистить ${placeholder}` }}
        disabled={disabled}
        style={{ minWidth: 210 }}
      />
    );
  });
}
